import React, { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/db/client";
import { Button } from "@/components/ui/button";
import { Plus, ChevronDown, Download, Highlighter, Layers, ListChecks, Loader2, RotateCcw, Trash2 } from "lucide-react";
import BacklogRow from "./BacklogRow";
import UserAvatar from "./UserAvatar";
import { HeaderDateFilter, HeaderFilter, type HeaderSortControl } from "./backlog/HeaderFilter";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
const AddItemDialog = lazy(() => import("./AddItemDialog"));
const DuplicateToPlatformsDialog = lazy(() => import("./DuplicateToPlatformsDialog"));
import BacklogFilters from "./BacklogFilters";
import LinkedItemsSubRow from "./backlog/LinkedItemsSubRow";
import { useBacklogItems, useInvalidateBacklogItems, type BacklogItem } from "@/hooks/useBacklogItems";
import { useBacklogFilters } from "@/hooks/useBacklogFilters";
import { useAnimatedList } from "@/hooks/useAnimatedList";
import { motionIsReduced } from "@/hooks/useReducedMotionPref";
import {
  useDeletedBacklogItems,
  useRestoreBacklogItems,
  usePermanentlyDeleteBacklogItems,
} from "@/hooks/useDeletedBacklogItems";
import { useFilterPresets } from "@/hooks/useFilterPresets";
import { useQueryClient } from "@tanstack/react-query";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import { useIsAdmin } from "@/hooks/useUserRole";
import BulkActionBar from "./backlog/BulkActionBar";
import { useAllItemLinkCounts } from "@/hooks/useBacklogItemLinks";
import { useDuplicateBacklogItem } from "@/hooks/useDuplicateBacklogItem";
import { useDropdownData } from "@/hooks/useDropdownData";
import { useAvailableStatuses } from "@/hooks/useAvailableStatuses";
import { useSubsheets, type Subsheet } from "@/hooks/useSubsheets";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { toast } from "sonner";
import { LOADING_HINT_DELAY_MS } from "@/lib/constants";
import { inScope, subtreeIds } from "@/lib/pageTree";
import { buildExportSheet, downloadSheet, exportFileName } from "@/lib/backlogExport";
import { useTeams, type Team } from "@/hooks/useTeams";
import { usePipelines } from "@/hooks/usePipelines";
import { effectiveDefaultPipelineId, orderByPipeline, type Pipeline } from "@/lib/pipelines";
import { requiresReason, resolveDoneStatusId } from "@/lib/statusKind";
import { cn } from "@/lib/utils";
import { parsePgArray } from "@/lib/pgArray";
import { firstVisibleSubsheetId } from "@/lib/backlogUtils";
import { paletteFor } from "@/lib/teamColors";
import { getDisplayName } from "@/lib/userUtils";
import { Checkbox } from "@/components/ui/checkbox";
import { useUserProfile } from "@/hooks/useUserProfile";
import { cursorTip } from "@/components/shared/CursorTip";
import type { SortColumn } from "@/types/backlog";
import { useCustomFields } from "@/hooks/useCustomFields";
import { FieldColumnHeader, FieldsColumnHeader } from "@/components/customFields/FieldListColumns";
import {
  fieldSortColumn,
  fieldSortLabels,
  listFieldLayout,
  makeFieldResolver,
  type CustomField,
} from "@/lib/customFields";

interface BacklogTableProps {
  itemIdFromUrl?: string;
  commentIdFromUrl?: string;
  onItemOpen?: (itemId: string) => void;
  onItemClose?: () => void;
  subsheetId?: string | null;
}

const EMPTY_ARRAY: never[] = [];

/**
 * How the list is cut into blocks.
 *
 * There is no "off": every dimension here answers a question about the work,
 * and "show me all of it in one list" is not one of them — that view exists as
 * the "All tickets" block that trails the status grouping, where it can carry
 * the Status column the grouped blocks drop.
 */
type GroupBy = "status" | "assignee" | "department";

const GROUP_BY_OPTIONS: Array<{ key: GroupBy; label: string }> = [
  { key: "status", label: "Status" },
  { key: "assignee", label: "Assignee" },
  { key: "department", label: "Department" },
];

/** The trailing "everything, ungrouped" block under the status grouping. */
const ALL_TICKETS_KEY = "__all_tickets__";

/**
 * The bulk-select cascade: how long each row's checkbox waits before its pop
 * plays (see `.tick-pop`), and how far down the list the stagger keeps
 * growing. The ticks themselves all land at once — the bulk bar counts the
 * whole selection immediately — so this is choreography for the
 * acknowledgement, not a delay on the state.
 *
 * The cap is the whole point of the second number: it holds the entire run
 * under ~400ms however long the range is, so a 60-row select-all reads as one
 * ripple rather than a procession — everything past the cap pops together on
 * the last step.
 */
const TICK_STEP_MS = 30;
const TICK_CASCADE_ROWS = 14;

/**
 * The section-collapse run: rows leave bottom-to-top (see `.row-collapse`),
 * each a step after the one below it, before the block actually closes. It is
 * the exact reverse of the reveal — expanding runs top-to-bottom, so closing
 * has to run floor-to-ceiling or the two read as unrelated animations.
 *
 * The run is bounded rather than capped. An earlier version CLAMPED the index
 * (`Math.min(count - 1 - i, 9)`), which held the whole run under ~400ms and
 * looked right on a ten-row section — but on a 25-row one it gave every row
 * above the tenth-from-the-bottom the SAME delay, and those are precisely the
 * rows on screen. The reader saw the visible sixteen vanish together while the
 * stagger played out below the fold, which is not a bottom-to-top exit; it is
 * a flash. Compressing the STEP instead keeps every row on its own beat at any
 * length, and still finishes inside the same window.
 */
const COLLAPSE_STEP_MS = 25;
/** Longest the whole staggered run may take, however many rows are in it. */
const COLLAPSE_RUN_MS = 225;
const COLLAPSE_EXIT_MS = 160;

/** The gap between one row's exit and the next one up, for a run of `count`. */
const collapseStepFor = (count: number) => (count > 1 ? Math.min(COLLAPSE_STEP_MS, COLLAPSE_RUN_MS / (count - 1)) : 0);

/** Delay for row `i` of `count` in a closing section, bottom row first. */
const collapseDelayFor = (i: number, count: number) => (count - 1 - i) * collapseStepFor(count);

/**
 * The block's ceiling — its column-header row — leaves one step after the
 * topmost item row, so the lid comes off last.
 */
const collapseCeilingDelay = (count: number) => collapseDelayFor(0, count) + collapseStepFor(count);

/**
 * How long a closing section's rows stay mounted: the longest delay in the
 * run, the exit itself, and a frame or two of slack so the last keyframe is
 * seen before the unmount.
 */
const collapseHoldFor = (count: number) => collapseCeilingDelay(count) + COLLAPSE_EXIT_MS + 40;

/**
 * The other block that is not one of `groups`: the "Recently deleted" holding
 * pen at the bottom. It and All tickets (`ALL_TICKETS_KEY` above) each keep
 * their own open/closed boolean rather than a key in `expandedGroups`, but the
 * closing RUN is shared state (`closingGroups`), so each needs a name in it.
 * Neither can collide with a group key — those are status/department ids, or
 * the `__none__` sentinel.
 */
const DELETED_KEY = "__deleted__";

/**
 * Task's readable minimum in the narrow-window form, in px (`w-40`).
 */
const COMPACT_TASK_FLOOR = 160;

/**
 * The narrow-window floor, as a min-width for the TABLE.
 *
 * It has to sit on the table rather than on Task's column. Task is the one
 * auto-width column in a `table-fixed` layout, and giving it a width leaves the
 * table with no auto column at all — at which point fixed layout stops handing
 * the leftover width to one column and spreads it PROPORTIONALLY over every
 * column instead. That is exactly what it used to do: between a 700px and a
 * 1023px window, Priority grew 64→120px, the action column 44→100px and the
 * checkbox 44→100px, all of them "fixed" widths that visibly were not. The
 * kebab, ranged right in a cell twice its intended width, drifted right with
 * it.
 *
 * With the floor on the table the same two things still happen — Task keeps a
 * readable minimum, and the block scrolls sideways when the window can't pay
 * for it — but Task stays the auto column, so it absorbs every spare pixel and
 * the sized columns hold the widths their classes name at every width.
 *
 * Each term mirrors a `max-lg:` width in the sizing rows below.
 */
const compactMinWidth = (withCheckbox: boolean, withStatus: boolean) =>
  (withCheckbox ? 44 : 0) + // select-all, w-11
  48 + // ID, max-lg:w-12
  80 + // Assigned, max-lg:w-20
  COMPACT_TASK_FLOOR +
  (withStatus ? 94 : 0) + // Status, max-lg:w-[5.875rem]
  64 + // Priority, max-lg:w-16
  44; // Row actions, max-lg:w-11

/**
 * The row highlights, and how long a ticket sits untouched before it counts as
 * stale.
 *
 * Both default OFF. A colour that appears on a table without being asked for
 * reads as a state the reader has to decode — and with a seeded backlog nearly
 * every row is stale, so the "on" default painted almost the whole column
 * amber and said nothing. They are opt-in: switch one on and the tint means
 * exactly the thing you just asked to see.
 */
interface HighlightPrefs {
  mine: boolean;
  stale: boolean;
  staleAfter: StaleAfter;
}

/**
 * How long a ticket has to sit untouched to count as stale.
 *
 * Three thresholds rather than two: on a queue where most work is measured in
 * weeks, "a week" tints nearly every row and says nothing, so the picker needs
 * a step coarse enough to still single a few rows out. A quarter is the
 * natural third rung — long enough that a ticket reaching it is genuinely
 * forgotten rather than merely slow.
 */
type StaleAfter = "week" | "month" | "quarter";

const HIGHLIGHTS_STORAGE_KEY = "backlog:highlights";

const DEFAULT_HIGHLIGHTS: HighlightPrefs = { mine: false, stale: false, staleAfter: "week" };

const DAY_MS = 24 * 60 * 60 * 1000;

const STALE_AFTER_MS: Record<StaleAfter, number> = {
  week: 7 * DAY_MS,
  month: 30 * DAY_MS,
  quarter: 90 * DAY_MS,
};

const STALE_AFTER_LABELS: Array<{ key: StaleAfter; label: string }> = [
  { key: "week", label: "Older than a week" },
  { key: "month", label: "Older than a month" },
  { key: "quarter", label: "Older than 3 months" },
];

const readHighlightPrefs = (): HighlightPrefs => {
  try {
    const stored = localStorage.getItem(HIGHLIGHTS_STORAGE_KEY);
    if (!stored) return DEFAULT_HIGHLIGHTS;
    const parsed = JSON.parse(stored) as Partial<HighlightPrefs>;
    return {
      mine: typeof parsed.mine === "boolean" ? parsed.mine : DEFAULT_HIGHLIGHTS.mine,
      stale: typeof parsed.stale === "boolean" ? parsed.stale : DEFAULT_HIGHLIGHTS.stale,
      // Validated against the table rather than listed a second time, so adding
      // a rung above cannot leave a stored value that reads back as the default.
      staleAfter:
        parsed.staleAfter && parsed.staleAfter in STALE_AFTER_MS ? parsed.staleAfter : DEFAULT_HIGHLIGHTS.staleAfter,
    };
  } catch {
    return DEFAULT_HIGHLIGHTS;
  }
};

/** A rendered section of the list: header marker + the rows beneath it. */
interface ItemGroup {
  key: string;
  label: string;
  /** Status colour, for the ring marker. */
  color?: string;
  /** Assignee profile, for the avatar marker. */
  profile?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string;
    avatar_url: string | null;
  };
  /** Department tile index, for the coloured square marker. */
  deptIndex?: number;
  items: BacklogItem[];
}

const DEPT_TILE_COLORS = [
  "bg-sky-500",
  "bg-violet-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-teal-500",
];

/** The same six, as values — the section's left rail is an inline colour. */
const DEPT_RAIL_COLORS = ["#0ea5e9", "#8b5cf6", "#10b981", "#f59e0b", "#f43f5e", "#14b8a6"];

/** Neutral rail for a section with no colour of its own. */
const NEUTRAL_RAIL = "#9aa6b4";

/**
 * The colour of a section's left rail, which is what ties a block to the
 * marker in its header: the status colour, the department's tile colour, or
 * the person's own deterministic palette colour.
 */
const railColorOf = (group: ItemGroup): string => {
  if (group.profile) return paletteFor(group.profile.id);
  if (group.deptIndex !== undefined) return DEPT_RAIL_COLORS[group.deptIndex % DEPT_RAIL_COLORS.length];
  return group.color ?? NEUTRAL_RAIL;
};

const BacklogTable = ({ itemIdFromUrl, commentIdFromUrl, onItemOpen, onItemClose, subsheetId }: BacklogTableProps) => {
  const { data: items = EMPTY_ARRAY as BacklogItem[], isLoading, isFetching, isError, error } = useBacklogItems();
  // One list, always grouped into collapsible sections — the old Table /
  // Split / By Person switcher offered three ways to read the same rows, of
  // which two were really just a grouping choice. That choice is now this.
  // A stored "none" from when grouping could be turned off falls back to
  // Status, which is the grouping that carries the All-tickets block that
  // replaced it.
  const [groupBy, setGroupBy] = useState<GroupBy>(() => {
    const stored = localStorage.getItem("backlog:groupBy");
    return stored === "assignee" || stored === "department" ? stored : "status";
  });
  // Grouping *by* status makes the Status column a restatement of the header
  // the reader just clicked, so it goes; Task takes the width back (it is the
  // one auto-width column in a `table-fixed` layout). Every other grouping
  // keeps it, because nothing else on the row says it.
  const showStatusColumn = groupBy !== "status";
  // Sections start collapsed — a 58-row backlog opens as a readable table of
  // contents rather than a wall, and you expand only what you're working in.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  // The trailing All-tickets block, which is its own table and so its own
  // open/shut state. Collapsed by default like every other section.
  const [allTicketsOpen, setAllTicketsOpen] = useState(false);
  // Row highlights. A view preference, not a filter: it changes how the rows
  // that are already on screen read, so it is stored with `groupBy` rather
  // than with the filter set, and a saved View never carries it.
  const [highlights, setHighlights] = useState<HighlightPrefs>(readHighlightPrefs);
  const updateHighlights = useCallback((patch: Partial<HighlightPrefs>) => {
    setHighlights((prev) => {
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem(HIGHLIGHTS_STORAGE_KEY, JSON.stringify(next));
      } catch (error) {
        console.error("Failed to save highlight preferences:", error);
      }
      return next;
    });
  }, []);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const { data: dropdownData } = useDropdownData();
  const { data: allSubsheets = [] as Subsheet[] } = useSubsheets();
  const visibleSubsheetIds = useMemo(() => new Set(allSubsheets.map((s) => s.id)), [allSubsheets]);
  // A department's view rolls up the departments nested beneath it (#9304),
  // the same reach a department grant has in item_visible().
  const subsheetScope = useMemo(
    () => (subsheetId ? subtreeIds(allSubsheets, subsheetId) : null),
    [allSubsheets, subsheetId],
  );

  // Pipelines (#9304): each row's progress mark, and — inside a department
  // with a default pipeline — the status groups in that pipeline's order.
  const { data: pipelines = EMPTY_ARRAY as Pipeline[] } = usePipelines();
  // Custom fields (#9399): one column per field in a department view, one
  // combined Fields column wherever the list mixes departments.
  const { data: customFields = EMPTY_ARRAY as CustomField[] } = useCustomFields();
  const { fieldColumns, summaryFields } = useMemo(() => {
    const layout = listFieldLayout(customFields, subsheetScope, allSubsheets);
    return { fieldColumns: layout.columns, summaryFields: layout.summary ?? undefined };
  }, [customFields, subsheetScope, allSubsheets]);
  // Which fields each row's combined cell shows, worked out once per
  // department combination rather than per row render.
  // Which fields apply to a ticket's departments, cached per department
  // combination. A value whose field doesn't apply stays hidden (decision 4):
  // rows, search, filters, sorts and the export all ask this.
  const fieldsFor = useMemo(() => makeFieldResolver(customFields, allSubsheets), [customFields, allSubsheets]);
  const pipelineById = useMemo(() => new Map(pipelines.map((p) => [p.id, p])), [pipelines]);
  const groupPipeline = useMemo(() => {
    if (!subsheetId) return undefined;
    const id = effectiveDefaultPipelineId(allSubsheets, subsheetId);
    return id ? pipelineById.get(id) : undefined;
  }, [subsheetId, allSubsheets, pipelineById]);
  const invalidateBacklogItems = useInvalidateBacklogItems();
  const queryClient = useQueryClient();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [insertPosition, setInsertPosition] = useState<number | null>(null);
  const [duplicateToPlatformsSource, setDuplicateToPlatformsSource] = useState<BacklogItem | null>(null);
  const duplicateMutation = useDuplicateBacklogItem();
  const [localItems, setLocalItems] = useState<BacklogItem[]>(items);
  const { isAdmin } = useIsAdmin();
  const { data: linkCounts } = useAllItemLinkCounts();
  const { presets, savePreset, deletePreset } = useFilterPresets();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoadingHint, setShowLoadingHint] = useState(false);
  const [expandedLinkIds, setExpandedLinkIds] = useState<Set<string>>(new Set());
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const tableScrollRestoredRef = useRef(false);
  // The two columns Tailwind drops at a breakpoint, read back in JS because
  // `colSpan` has to agree with them (see `tableColSpan`). Same breakpoints as
  // the `xl:` / `2xl:` classes on those cells.
  const showCreatedColumn = useMediaQuery("(min-width: 1280px)");
  const showAddedByColumn = useMediaQuery("(min-width: 1536px)");
  /**
   * Below the sidebar's own breakpoint the table has the whole window and it is
   * still not enough: `lg` is where the fixed columns stop leaving Task a
   * readable share of the row. Rows are told rather than each asking, because a
   * media-query listener per row is a listener per row.
   */
  const compactRows = !useMediaQuery("(min-width: 1024px)");

  // Save table scroll position on every scroll event
  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const onScroll = () => {
      sessionStorage.setItem("backlog:tableScrollTop", String(el.scrollTop));
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  });

  // Restore table scroll position after items sync has settled
  useEffect(() => {
    if (tableScrollRestoredRef.current || localItems.length === 0) return;
    const saved = sessionStorage.getItem("backlog:tableScrollTop");
    if (!saved) return;

    const target = Number(saved);
    if (target === 0) return;

    // Delay to ensure the setLocalItems(items) sync at line ~209 and
    // subsequent re-render have completed before restoring scroll
    const timer = setTimeout(() => {
      tableScrollRestoredRef.current = true;
      const el = tableScrollRef.current;
      if (el) {
        el.scrollTop = target;
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [localItems]);

  const handleToggleLinkedItems = useCallback((itemId: string) => {
    setExpandedLinkIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  }, []);

  // Check authentication status
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Filter state and logic (extracted to hook)
  const {
    filteredItems,
    searchQuery,
    setSearchQuery,
    selectedAssignees,
    setSelectedAssignees,
    selectedStatuses,
    setSelectedStatuses,
    selectedPriorities,
    setSelectedPriorities,
    selectedAddedBys,
    setSelectedAddedBys,
    selectedTicketTypes,
    setSelectedTicketTypes,
    selectedPipelines,
    setSelectedPipelines,
    selectedTeams,
    setSelectedTeams,
    createdRange,
    setCreatedRange,
    updatedRange,
    setUpdatedRange,
    sortStack,
    toggleSort,
    removeSort,
    fieldFilters,
    setFieldFilter,
    clearFilters: handleClearFilters,
    loadFilters,
  } = useBacklogFilters({
    items: localItems,
    dropdownData,
    subsheetScope,
    customFields,
    fieldsFor,
  });

  /**
   * The list as it is actually drawn.
   *
   * Everything below reads `displayItems`, not `filteredItems`: applying a
   * filter used to rewrite the whole table between two frames, and the rows
   * that survived it jumped up the page with nothing on screen connecting
   * where they had been to where they now were. `useAnimatedList` holds the
   * outgoing rows for one short beat so they can fade (`exitingIds` marks
   * them), then commits and glides every element tagged `data-flip-id` from
   * its old position to its new one. Sorting takes the same path — no rows
   * leave, so it is the glide alone.
   */
  const { items: displayItems, exitingIds } = useAnimatedList(filteredItems, tableScrollRef);

  // Export to Excel (#9304): exactly the list on screen — this department and
  // the ones beneath it, every active filter and search, in the current sort.
  // filteredItems rather than displayItems, so rows still animating out of a
  // just-applied filter are not exported.
  const { data: teams = EMPTY_ARRAY as Team[] } = useTeams();
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      // dropdownData.profiles drops deactivated accounts (it feeds the
      // assignee pickers), but a former member still filed and worked tickets
      // and the export should name them.
      const { data: everyone, error: profilesError } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, email");
      if (profilesError) throw profilesError;
      // Only values whose field applies to the ticket: the rest are hidden.
      const exported = filteredItems.map((i) => {
        const applies = fieldsFor(i.subsheet_ids ?? []);
        return { ...i, custom_fields: Object.fromEntries(applies.map((f) => [f.id, i.custom_fields[f.id]])) };
      });
      const sheet = buildExportSheet(exported, {
        options: dropdownData?.allOptions ?? [],
        profiles: everyone ?? [],
        subsheets: allSubsheets,
        teams,
        pipelines,
        // The fields that apply to at least one exported ticket, in field order.
        customFields: fieldsFor([...new Set(filteredItems.flatMap((i) => i.subsheet_ids ?? []))]),
      });
      const scopeName = subsheetId ? (allSubsheets.find((s) => s.id === subsheetId)?.name ?? null) : null;
      await downloadSheet(sheet, exportFileName(scopeName, new Date()));
      toast.success(`Exported ${filteredItems.length} ticket${filteredItems.length === 1 ? "" : "s"}`);
    } catch (error) {
      console.error("Backlog export failed:", error);
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const hasActiveFilters =
    selectedAssignees.length > 0 ||
    selectedStatuses.length > 0 ||
    selectedPriorities.length > 0 ||
    selectedAddedBys.length > 0 ||
    selectedTicketTypes.length > 0 ||
    selectedPipelines.length > 0 ||
    selectedTeams.length > 0 ||
    createdRange.from !== null ||
    createdRange.to !== null ||
    updatedRange.from !== null ||
    updatedRange.to !== null ||
    Object.keys(fieldFilters).length > 0;

  /**
   * The sort control handed to one column header.
   *
   * The stack's shape (an ordered list of rules) is the hook's; a header only
   * needs to know where it sits in that order and how to change it. Computing
   * the position here rather than in the header is what keeps the numbers on
   * the badges consistent — they are positions in one list, not per-column
   * state that could drift apart.
   */
  const sortControlFor = useCallback(
    (column: SortColumn, ascLabel: string, descLabel: string): HeaderSortControl => {
      const at = sortStack.findIndex((rule) => rule.column === column);
      return {
        index: at === -1 ? null : at + 1,
        direction: at === -1 ? null : sortStack[at].direction,
        onToggle: (direction) => toggleSort(column, direction),
        onRemove: () => removeSort(column),
        ascLabel,
        descLabel,
      };
    },
    [sortStack, toggleSort, removeSort],
  );

  // Who the reader is, for the assigned-to-me row tint. Empty when the
  // highlight is switched off, so the rows are never told they are anyone's.
  const { userId: currentUserId } = useUserProfile();
  const myItemIds = useMemo(() => {
    if (!currentUserId || !highlights.mine) return new Set<string>();
    const mine = new Set<string>();
    for (const item of displayItems) {
      if (parsePgArray(item.assigned_to_id).includes(currentUserId)) mine.add(item.id);
    }
    return mine;
  }, [displayItems, currentUserId, highlights.mine]);

  /** The staleness cutoff a row measures against, or null while it is off. */
  const staleAfterMs = highlights.stale ? STALE_AFTER_MS[highlights.staleAfter] : null;

  // Sync local items with fetched items only when data changes
  useEffect(() => {
    setLocalItems(items);
  }, [items]);

  // Clear selected items when switching subsheets
  useEffect(() => {
    setSelectedItemIds([]);
  }, [subsheetId]);

  // Note: Real-time subscription is handled in useBacklogItems hook
  // No need for duplicate subscription here

  // Debug query state and provide a timeout-based retry hint
  useEffect(() => {
    if (isLoading) {
      const t = setTimeout(() => setShowLoadingHint(true), LOADING_HINT_DELAY_MS);
      return () => clearTimeout(t);
    }
    setShowLoadingHint(false);
  }, [isLoading]);

  // Status a new ticket should start on, when it was added from inside a
  // status section rather than from the toolbar.
  const [addStatusId, setAddStatusId] = useState<string | null>(null);

  const handleAddItem = (position?: number, statusId?: string | null) => {
    setInsertPosition(position || null);
    setAddStatusId(statusId ?? null);
    setShowAddDialog(true);
  };

  const handleDuplicateItem = async (item: BacklogItem) => {
    await duplicateMutation
      .mutateAsync({
        source: item,
        insertPosition: item.row_number + 1,
        emitToast: true,
      })
      .catch(() => {
        // Error toast is fired by the hook's onError; swallow here so the
        // calling row component doesn't re-throw to the console.
      });
  };

  const handleDuplicateToPlatforms = (item: BacklogItem) => {
    setDuplicateToPlatformsSource(item);
  };

  const handleItemCreated = () => {
    setShowAddDialog(false);
    setInsertPosition(null);
    setAddStatusId(null);
  };

  // Anchor for shift-click ranges: the last row checked without a modifier.
  const lastCheckedIdRef = useRef<string | null>(null);

  /**
   * The rows whose checkboxes are mid-cascade, and where each sits in the run.
   *
   * A bulk tick — select-all, or a shift-click range — applies to the whole
   * set in one commit; this only decides the ORDER the pops ripple in (see
   * `.tick-pop`). Null means the last selection change was a single click,
   * which has nothing to queue behind and must not inherit a delay from the
   * bulk action before it.
   */
  const [tickOrder, setTickOrder] = useState<Map<string, number> | null>(null);
  const beginTickCascade = (ids: string[]) => setTickOrder(new Map(ids.map((id, i) => [id, i])));

  // `ordered` is the list the range is read off — the rows of the block the
  // click happened in, which is not always the grouped sections (the All
  // tickets block is a table of its own).
  const handleCheckboxChange = (itemId: string, checked: boolean, shiftKey = false, ordered: BacklogItem[] = []) => {
    const anchor = lastCheckedIdRef.current;

    // Shift-click extends from the anchor to here, in the order the rows are
    // actually displayed — so the selection matches what the eye swept over.
    if (shiftKey && anchor && anchor !== itemId) {
      const from = ordered.findIndex((i) => i.id === anchor);
      const to = ordered.findIndex((i) => i.id === itemId);
      if (from !== -1 && to !== -1) {
        const range = ordered.slice(Math.min(from, to), Math.max(from, to) + 1).map((i) => i.id);
        setSelectedItemIds((prev) =>
          checked ? [...new Set([...prev, ...range])] : prev.filter((id) => !range.includes(id)),
        );
        // The run reads FROM THE ANCHOR TO THE ROW JUST CLICKED, which for the
        // ordinary downward sweep is display order and for an upward one is
        // display order reversed. It is not "top to bottom": the row under the
        // cursor is the one whose tick the eye is already on, so if it lands
        // first there is no cascade to see — everything after it is a ripple
        // running away from where the reader is looking. Arriving last, it
        // reads as the selection catching up to the click.
        if (checked) beginTickCascade(from > to ? [...range].reverse() : range);
        else setTickOrder(null);
        lastCheckedIdRef.current = itemId;
        return;
      }
    }

    // One box, by hand: no run to join.
    setTickOrder(null);
    lastCheckedIdRef.current = checked ? itemId : null;
    if (checked) {
      setSelectedItemIds((prev) => [...prev, itemId]);
    } else {
      setSelectedItemIds((prev) => prev.filter((id) => id !== itemId));
    }
  };

  // Select-all is per section now that the column headers are: it reaches the
  // rows of the block whose checkbox was clicked and nothing else. Another
  // section's selection is neither claimed nor released, so collapsing or
  // expanding elsewhere can never silently rewrite what the user picked.
  const handleSelectGroup = (group: ItemGroup, checked: boolean) => {
    const ids = group.items.map((item) => item.id);
    if (checked) {
      setSelectedItemIds((prev) => [...new Set([...prev, ...ids])]);
      // Every box in one commit; the ticks then run down the section in the
      // order the rows are read.
      beginTickCascade(ids);
    } else {
      const dropped = new Set(ids);
      setSelectedItemIds((prev) => prev.filter((id) => !dropped.has(id)));
      // Clearing is not a cascade: an empty box has no mark to arrive.
      setTickOrder(null);
    }
  };

  // Multi-select + bulk edit is open to any authenticated user (unauthenticated visitors have no
  // session; clients never reach this table). Destructive bulk actions stay admin-only.
  const canBulkEdit = isAuthenticated;

  const handleMarkComplete = () => {
    if (!selectedItemIds.length) return;
    const doneId = resolveDoneStatusId(selectableStatuses);
    if (!doneId) {
      toast.error("No “done” status is configured for this department");
      return;
    }
    handleBulkStatusChange(doneId);
  };

  // No dialog on the way in: the bulk Delete is a 1.5s press-and-hold, and
  // what it does is recoverable from Recently deleted. The one delete that
  // isn't — permanent deletion from that section — keeps its dialog.
  const handleBulkDelete = async () => {
    const itemsToDelete = [...selectedItemIds];
    const deleteCount = itemsToDelete.length;
    if (!deleteCount) return;

    // Optimistically update UI immediately
    queryClient.setQueryData<BacklogItem[]>(["backlog-items"], (old) => {
      if (!old) return old;
      return old.filter((item) => !itemsToDelete.includes(item.id));
    });

    // Clear the selection immediately — the bulk bar it belongs to is what
    // the user just held, and it swaps back to the filter bar from here.
    setSelectedItemIds([]);
    toast.success(`${deleteCount} item(s) moved to Recently Deleted`);

    // Perform actual deletion in background
    try {
      const { error } = await supabase
        .from("backlog_items")
        .update({ deleted_at: new Date().toISOString() })
        .in("id", itemsToDelete);

      if (error) throw error;

      // Only invalidate deleted items query
      queryClient.invalidateQueries({ queryKey: ["deleted-backlog-items"] });
    } catch (error) {
      console.error("Error deleting items:", error);
      toast.error("Failed to delete items");
      // Revert on error
      invalidateBacklogItems();
    }
  };

  // Statuses selectable for the current platform (per-platform config; defaults to all).
  // Badge rendering still uses the full dropdownData set so disabled-but-in-use statuses render.
  const selectableStatuses = useAvailableStatuses(subsheetId ?? null);

  // Option lists for the header dropdowns. People are alphabetical to match
  // the assignee picker. The Assigned list is exactly that list of people:
  // an "Unassigned" row used to lead it, and it is gone with the state it
  // named — a ticket is assigned to someone when it is filed here, so the
  // option offered a facet that matches nothing.
  const sortedProfiles = useMemo(
    () => [...(dropdownData?.profiles ?? [])].sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b))),
    [dropdownData?.profiles],
  );
  const personFilterOptions = useMemo(
    () => sortedProfiles.map((p) => ({ id: p.id, label: getDisplayName(p), profile: p })),
    [sortedProfiles],
  );
  const statusFilterOptions = useMemo(
    () => selectableStatuses.map((s) => ({ id: s.id, label: s.label, color: s.color })),
    [selectableStatuses],
  );
  const priorityFilterOptions = useMemo(
    () => (dropdownData?.priorities ?? []).map((p) => ({ id: p.id, label: p.label, color: p.color })),
    [dropdownData?.priorities],
  );

  // Recently deleted rides at the bottom of the list as its own collapsible
  // section — a holding pen in view of the queue it came from, not a separate
  // page. Scoped to the current department like everything else on screen.
  const { data: allDeletedItems = EMPTY_ARRAY as BacklogItem[] } = useDeletedBacklogItems();
  const deletedItems = useMemo(
    () => (subsheetScope ? allDeletedItems.filter((i) => inScope(i.subsheet_ids, subsheetScope)) : allDeletedItems),
    [allDeletedItems, subsheetScope],
  );
  const [showDeleted, setShowDeleted] = useState(false);
  const restoreMutation = useRestoreBacklogItems();
  const permanentDeleteMutation = usePermanentlyDeleteBacklogItems();
  const [permanentDeleteTarget, setPermanentDeleteTarget] = useState<BacklogItem | null>(null);

  const handleRestore = async (item: BacklogItem) => {
    try {
      await restoreMutation.mutateAsync([item.id]);
      toast.success(`#${item.ticket_number} restored to the backlog`);
    } catch (error) {
      console.error("Error restoring item:", error);
      toast.error("Failed to restore item");
    }
  };

  const handlePermanentDelete = async () => {
    if (!permanentDeleteTarget) return;
    const target = permanentDeleteTarget;
    setPermanentDeleteTarget(null);
    try {
      await permanentDeleteMutation.mutateAsync([target.id]);
      toast.success(`#${target.ticket_number} permanently deleted`);
    } catch (error) {
      console.error("Error permanently deleting item:", error);
      toast.error("Failed to permanently delete item");
    }
  };

  /** "today" / "yesterday" / "6d ago" for the deleted-section rows. */
  const deletedAgo = (iso: string | null) => {
    if (!iso) return "";
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
    if (days <= 0) return "today";
    if (days === 1) return "yesterday";
    return `${days}d ago`;
  };

  /** Which section an item belongs to, for the current grouping. */
  const groupKeyOf = useCallback(
    (item: BacklogItem) => {
      if (groupBy === "assignee") return parsePgArray(item.assigned_to_id)[0] ?? "__none__";
      if (groupBy === "department") return firstVisibleSubsheetId(item.subsheet_ids, visibleSubsheetIds) ?? "__none__";
      return item.status_id ?? "__none__";
    },
    [groupBy, visibleSubsheetIds],
  );

  // Sections for the list. Each dimension keeps its own natural order — the
  // tenant's workflow order for statuses, the admin's order_index for
  // departments, display name for people — and the "none" bucket always trails.
  const groups = useMemo((): ItemGroup[] => {
    const byKey = new Map<string, BacklogItem[]>();
    for (const item of displayItems) {
      const key = groupKeyOf(item);
      const list = byKey.get(key);
      if (list) list.push(item);
      else byKey.set(key, [item]);
    }

    const out: ItemGroup[] = [];
    const pushNone = (label: string) => {
      const none = byKey.get("__none__");
      if (none?.length) out.push({ key: "__none__", label, color: "#9aa6b4", items: none });
    };

    if (groupBy === "assignee") {
      const profiles = dropdownData?.profiles ?? [];
      const named = profiles
        .filter((p) => byKey.has(p.id))
        .sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)));
      for (const p of named) {
        out.push({ key: p.id, label: getDisplayName(p), profile: p, items: byKey.get(p.id)! });
      }
      pushNone("Unassigned");
      return out;
    }

    if (groupBy === "department") {
      (allSubsheets as Subsheet[]).forEach((s, i) => {
        const list = byKey.get(s.id);
        if (list?.length) out.push({ key: s.id, label: s.name, deptIndex: i, items: list });
      });
      pushNone("No department");
      return out;
    }

    for (const s of orderByPipeline(dropdownData?.statuses ?? [], groupPipeline?.statusIds ?? [])) {
      const list = byKey.get(s.id);
      if (list?.length) out.push({ key: s.id, label: s.label, color: s.color || "#9aa6b4", items: list });
    }
    pushNone("No status");
    return out;
  }, [displayItems, groupBy, groupKeyOf, dropdownData?.statuses, dropdownData?.profiles, allSubsheets, groupPipeline]);

  // Every row a user can currently reach, in display order — the items of
  // expanded sections only. Shift-click ranges read from this one list, so a
  // range can never sweep up a row that isn't on screen.
  const expandedRows = useMemo(
    () => groups.flatMap((g) => (expandedGroups.has(g.key) ? g.items : [])),
    [groups, expandedGroups],
  );

  /** The grouped table's narrow-window form — see `allTicketsCompact`. */
  const groupedCompact = compactRows && expandedRows.length > 0;

  // The trailing All-tickets block, as a group: it draws the same chrome, the
  // same column-header row and the same rows as the sections above, so it is
  // the same shape of thing and not a second implementation of one.
  const allTicketsGroup = useMemo(
    (): ItemGroup => ({ key: ALL_TICKETS_KEY, label: "All tickets", color: NEUTRAL_RAIL, items: displayItems }),
    [displayItems],
  );
  const allTicketsRailStyle = { "--group-rail": NEUTRAL_RAIL } as CSSProperties;

  /** Membership test behind each block's select-all checkbox. */
  const selectedIdSet = useMemo(() => new Set(selectedItemIds), [selectedItemIds]);

  // displayRowNumber stays the position in the full filtered list, so the
  // numbers a user sees don't restart inside every section.
  const filteredIndexById = useMemo(() => {
    const map = new Map<string, number>();
    displayItems.forEach((item, i) => map.set(item.id, i));
    return map;
  }, [displayItems]);

  /**
   * Sections mid-close: still in `expandedGroups` (their rows are mounted,
   * playing the bottom-to-top exit), already committed to closing. The header
   * chrome reads these as collapsed at once — the click's feedback must not
   * wait 400ms for the rows to finish leaving.
   */
  const [closingGroups, setClosingGroups] = useState<Set<string>>(new Set());
  /**
   * Whether a block is drawn in its narrow-window form: a floor under the table
   * (see `compactMinWidth`) and a scroller of its own. Only while
   * the block is actually OPEN — the sizing row is static, so a shut section
   * would otherwise hold the table wide and print a horizontal scrollbar under
   * a stack of collapsed headers with nothing in them.
   */
  const allTicketsCompact = compactRows && allTicketsOpen;

  /** The two standalone blocks' places in that set (see the keys' comment). */
  const allTicketsClosing = closingGroups.has(ALL_TICKETS_KEY);
  const deletedClosing = closingGroups.has(DELETED_KEY);
  const closeTimersRef = useRef(new Map<string, number>());
  useEffect(
    () => () => {
      closeTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    },
    [],
  );

  // Pin scrollLeft across a re-render that changes the table's content width:
  // the browser re-clamps the horizontal scroll, which reads as the list
  // lurching sideways.
  const pinScrollLeft = () => {
    const keepScrollLeft = tableScrollRef.current?.scrollLeft ?? 0;
    requestAnimationFrame(() => {
      if (tableScrollRef.current) tableScrollRef.current.scrollLeft = keepScrollLeft;
    });
  };

  /**
   * Open or close one collapsible block, with the bottom-to-top exit run on
   * the way shut.
   *
   * Only the TIMING lives here. Where a block keeps its own open/closed state
   * is the caller's business — the grouped sections share a Set, All tickets
   * and Recently deleted each have a boolean — and `commit` is the flip. That
   * split is the whole reason this is a helper: the two standalone blocks are
   * separate tables with their own render paths, and when the run lived inside
   * the grouped path they simply had no exit animation at all.
   */
  const toggleSection = (key: string, isOpen: boolean, rowCount: number, commit: () => void) => {
    const timers = closeTimersRef.current;

    // Re-opening a section that is mid-close just cancels the close: the rows
    // never unmounted, so there is nothing to re-reveal.
    if (timers.has(key)) {
      window.clearTimeout(timers.get(key));
      timers.delete(key);
      setClosingGroups((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      return;
    }

    // Either route counts: the OS setting, or the app's own "Reduce motion"
    // switch (Profile). The cascade is the one part of this that CSS cannot be
    // told to skip — the rows are held MOUNTED for the length of the run, so
    // stopping the animation alone would leave the section standing open and
    // blank until the timer fired.
    const reduceMotion = motionIsReduced();

    // Collapsing an open section: hold the rows for one bottom-to-top exit
    // run (see `.row-collapse`), then actually close. Reduced motion skips
    // the performance and collapses on the click, as before.
    if (isOpen && rowCount > 0 && !reduceMotion) {
      setClosingGroups((prev) => new Set(prev).add(key));
      const timer = window.setTimeout(() => {
        timers.delete(key);
        pinScrollLeft();
        setClosingGroups((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
        commit();
      }, collapseHoldFor(rowCount));
      timers.set(key, timer);
      return;
    }

    pinScrollLeft();
    commit();
  };

  const toggleGroup = (key: string, rowCount = 0) =>
    toggleSection(key, expandedGroups.has(key), rowCount, () =>
      setExpandedGroups((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    );

  const bulkStatuses = useMemo(() => selectableStatuses.filter((s) => !requiresReason(s.kind)), [selectableStatuses]);

  const handleBulkStatusChange = async (statusId: string) => {
    const itemIds = [...selectedItemIds];
    if (!itemIds.length) return;
    const updateCount = itemIds.length;

    queryClient.setQueryData<BacklogItem[]>(["backlog-items"], (old) => {
      if (!old) return old;
      const idSet = new Set(itemIds);
      return old.map((it) => (idSet.has(it.id) ? { ...it, status_id: statusId } : it));
    });

    setSelectedItemIds([]);
    toast.success(`${updateCount} item(s) status updated`);

    try {
      const BATCH_SIZE = 50;
      for (let i = 0; i < itemIds.length; i += BATCH_SIZE) {
        const batch = itemIds.slice(i, i + BATCH_SIZE);
        const { error } = await supabase.from("backlog_items").update({ status_id: statusId }).in("id", batch);
        if (error) throw error;
      }
    } catch (error) {
      console.error("Bulk status change failed:", error);
      toast.error("Failed to update status");
      invalidateBacklogItems();
    }
  };

  const handleBulkPriorityChange = async (priorityId: string) => {
    const itemIds = [...selectedItemIds];
    if (!itemIds.length) return;
    const updateCount = itemIds.length;

    queryClient.setQueryData<BacklogItem[]>(["backlog-items"], (old) => {
      if (!old) return old;
      const idSet = new Set(itemIds);
      return old.map((it) => (idSet.has(it.id) ? { ...it, priority_id: priorityId } : it));
    });

    setSelectedItemIds([]);
    toast.success(`${updateCount} item(s) priority updated`);

    try {
      const BATCH_SIZE = 50;
      for (let i = 0; i < itemIds.length; i += BATCH_SIZE) {
        const batch = itemIds.slice(i, i + BATCH_SIZE);
        const { error } = await supabase.from("backlog_items").update({ priority_id: priorityId }).in("id", batch);
        if (error) throw error;
      }
    } catch (error) {
      console.error("Bulk priority change failed:", error);
      toast.error("Failed to update priority");
      invalidateBacklogItems();
    }
  };

  // Rows are no longer hand-ordered — there is no drag context, no
  // `handleDragEnd`, and no row_number write from this list. `row_number`
  // itself stays: the column, the admin Resort that rewrites it, and the
  // position a duplicate is filed at all still use it.

  if (isLoading && localItems.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex items-center gap-4 text-muted-foreground">
          <p>Loading backlog...</p>
          {showLoadingHint && (
            <Button size="sm" variant="outline" onClick={() => invalidateBacklogItems()}>
              Retry
            </Button>
          )}
        </div>
      </div>
    );
  }

  const groupByLabel = GROUP_BY_OPTIONS.find((o) => o.key === groupBy)?.label ?? "Status";
  const highlightCount = (highlights.mine ? 1 : 0) + (highlights.stale ? 1 : 0);

  const exportButton = (
    <button
      type="button"
      onClick={handleExport}
      disabled={exporting || filteredItems.length === 0}
      title="Download the tickets on screen as an Excel file"
      className="press-pop inline-flex h-9 items-center gap-1.5 rounded-full border border-border bg-card px-3.5 text-[13px] font-medium text-foreground transition-all hover:-translate-y-px hover:border-[hsl(var(--ring))] disabled:pointer-events-none disabled:opacity-50"
    >
      {exporting ? (
        <Loader2 className="h-[15px] w-[15px] shrink-0 animate-spin text-muted-foreground" />
      ) : (
        <Download className="h-[15px] w-[15px] shrink-0 text-muted-foreground" />
      )}
      <span className="whitespace-nowrap">Export</span>
    </button>
  );

  const viewSwitcher = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title="Choose how the list is grouped"
          className="press-pop inline-flex h-9 items-center gap-1.5 rounded-full border border-border bg-card px-3.5 text-[13px] font-medium text-foreground transition-all hover:-translate-y-px hover:border-[hsl(var(--ring))]"
        >
          <Layers className="h-[15px] w-[15px] shrink-0 text-muted-foreground" />
          <span className="whitespace-nowrap">
            Group: <span className="font-bold">{groupByLabel}</span>
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuLabel>Group by</DropdownMenuLabel>
        {GROUP_BY_OPTIONS.map((option) => (
          <DropdownMenuItem
            key={option.key}
            onClick={() => {
              setGroupBy(option.key);
              localStorage.setItem("backlog:groupBy", option.key);
              setExpandedGroups(new Set());
            }}
            className={cn("gap-2", groupBy === option.key && "font-bold text-brand")}
          >
            {option.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  /**
   * The row highlights, as a control beside Group.
   *
   * It sits with Group rather than in the Filter popover because the two are
   * the same kind of decision — how the rows already on screen are arranged
   * and marked — while Filter decides which rows there are at all. Putting a
   * pair of switches in among Filter's value list would have mixed "narrow the
   * data" with "paint the data" in one popover.
   */
  const highlightSwitcher = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* Icon and a count, no label. It sits in a row of labelled controls
            and is the only one that changes nothing about *which* rows are
            shown — so it stays the quiet one, and the cursor tip carries the
            word "Highlight" for anyone who needs it. */}
        <button
          type="button"
          aria-label="Choose which rows are highlighted"
          className={cn(
            "press-pop inline-flex h-9 items-center gap-1.5 rounded-full border bg-card px-3 text-[13px] font-medium transition-all hover:-translate-y-px hover:border-[hsl(var(--ring))]",
            highlightCount > 0 ? "border-[hsl(var(--ring))] text-brand" : "border-border text-foreground",
          )}
          {...cursorTip(
            highlightCount > 0 ? `${highlightCount} on — my tickets, stale tickets` : "my tickets, stale tickets",
            "Highlight rows:",
          )}
        >
          <Highlighter
            className={cn("h-[15px] w-[15px] shrink-0", highlightCount > 0 ? "text-brand" : "text-muted-foreground")}
          />
          {highlightCount > 0 && (
            <span className="rounded-full bg-primary px-1.5 text-[10px] font-bold leading-4 text-primary-foreground">
              {highlightCount}
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Highlight rows</DropdownMenuLabel>
        <DropdownMenuCheckboxItem
          checked={highlights.mine}
          onCheckedChange={(checked) => updateHighlights({ mine: checked === true })}
          onSelect={(e) => e.preventDefault()}
        >
          My tickets
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={highlights.stale}
          onCheckedChange={(checked) => updateHighlights({ stale: checked === true })}
          onSelect={(e) => e.preventDefault()}
        >
          Stale tickets
        </DropdownMenuCheckboxItem>
        {/* The threshold only exists while the highlight it measures does. */}
        {highlights.stale && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Stale means
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={highlights.staleAfter}
              onValueChange={(value) => updateHighlights({ staleAfter: value as HighlightPrefs["staleAfter"] })}
            >
              {STALE_AFTER_LABELS.map((option) => (
                <DropdownMenuRadioItem key={option.key} value={option.key} onSelect={(e) => e.preventDefault()}>
                  {option.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // Exact, in both directions, and it has to follow the breakpoints: a header
  // row spanning fewer columns than the table has would close its block
  // mid-table, and one spanning *more* doesn't clamp — it appends a phantom
  // column that steals width from Task. Added By, Created and Last updated are
  // dropped by media query, so the count is a media query too. Created and
  // Last updated share one breakpoint: they are a pair, and one date without
  // the other is the ambiguity the pair exists to remove.
  // Custom field columns share Created's breakpoint, so they count with it.
  const fieldColumnCount = fieldColumns.length + (summaryFields ? 1 : 0);
  const colSpanFor = (withStatus: boolean) =>
    5 +
    (withStatus ? 1 : 0) +
    (canBulkEdit ? 1 : 0) +
    (showCreatedColumn ? 2 + fieldColumnCount : 0) +
    (showAddedByColumn ? 1 : 0);
  const tableColSpan = colSpanFor(showStatusColumn);
  /** The All-tickets table always carries Status, so it counts its own. */
  const allTicketsColSpan = colSpanFor(true);

  /**
   * A block's own column headers — labels, filter dropdowns, and a select-all
   * scoped to that section.
   *
   * They live inside the block rather than floating above the list because a
   * header for columns you can't see is noise: with every section collapsed
   * there is nothing to label, and the row is gone. The dropdowns are the same
   * controls wherever they appear — filter state is one set of values, so a
   * status filter set from one block reads as set in every other.
   *
   * The row scrolls with its block rather than pinning to the top of the
   * scrollport. `position: sticky` on a table cell is constrained by the
   * *table*, not by the cell's row group — tried it, and a block's headers
   * stayed pinned over the collapsed sections long after the block itself had
   * scrolled away. A header that outlives its rows is worse than one that
   * leaves with them.
   */
  const renderColumnHeaderRow = (
    group: ItemGroup,
    railStyle: CSSProperties,
    withStatus = showStatusColumn,
    exiting = false,
    // Set while the section is closing: this row is the block's ceiling, so it
    // leaves LAST — one step after the topmost row (see `.row-collapse`).
    collapseDelayMs?: number,
  ) => {
    const ids = group.items.map((item) => item.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedIdSet.has(id));
    const someSelected = !allSelected && ids.some((id) => selectedIdSet.has(id));
    // px-2 is the floor, not the norm: a header cell's padding is whatever the
    // column's DATA cell uses, so the label sits over the values it names.
    // Task, Assigned and Added By override to their rows' px-4 / px-3; the two
    // dates and Priority stay at the tighter step their rows use, because at
    // their widths the padding is most of what the content has to live in.
    const cell = "bg-card px-2 py-2 text-left text-xs font-semibold text-foreground";
    return (
      <tr
        data-flip-id={`ch:${group.key}`}
        style={collapseDelayMs === undefined ? railStyle : { ...railStyle, animationDelay: `${collapseDelayMs}ms` }}
        // The vertical rules follow BacklogRow's: the narrow utility columns
        // belong to the cell on their left, so that cell opts out of its own
        // rule and the pair emits one.
        className={cn(
          "group-block [&>th]:border-b [&>th]:border-[hsl(var(--table-hairline))] [&>th:not([data-vrule=off])]:border-r",
          exiting && "row-exit",
          collapseDelayMs !== undefined && "row-collapse",
        )}
      >
        {canBulkEdit && (
          // pl-4 left a 2rem cell with 14px of content box for a 16px
          // checkbox, so the box hung over the ID column's rule. Symmetric
          // padding centres it: the block's rail and the column rule are both
          // BORDERS on this cell, so the content box already begins at the
          // rail's inner edge and no second offset for it is wanted (see the
          // row's matching comment in BacklogRow).
          <th scope="col" className={cn(cell, "w-11 py-2 px-2 text-center")}>
            <div className="flex items-center justify-center">
              <Checkbox
                checked={allSelected}
                onCheckedChange={(checked) => handleSelectGroup(group, checked === true)}
                aria-label={`Select all in ${group.label}`}
                className={someSelected ? "data-[state=checked]:bg-primary" : ""}
                style={someSelected ? { opacity: 0.5 } : {}}
              />
            </div>
          </th>
        )}
        <th scope="col" className={cn(cell, "w-16 px-1 text-center")}>
          ID
        </th>
        {/* Headers that can filter or sort ARE those controls — click the
            column you want to narrow or re-order, right where its values live.
            The toolbar has no Sort button any more for the same reason the
            filter row went: the control belongs on the data.
            Only Priority and the two dates carry a sort; the rest filter. */}
        {/* Task is the ONLY left-ranged header. Every sized column centres its
            header over the column's middle — on the narrow fixed columns a
            left-ranged label left all the slack on one side, which read as the
            header belonging to the wrong column. Task stays left because its
            column is the auto one: its label has no middle to sit on, and its
            data is prose. Data cells keep their own alignment. */}
        <th scope="col" className={cn(cell, "w-[6.75rem] px-3 text-center")}>
          <HeaderFilter
            label="Assigned"
            options={personFilterOptions}
            selected={selectedAssignees}
            onChange={setSelectedAssignees}
            searchPlaceholder="Search people..."
            align="center"
            triggerClassName="justify-center"
          />
        </th>
        <th scope="col" className={cn(cell, "px-4")}>
          Task
        </th>
        {withStatus && (
          // A tenth narrower than it was, and px-3 rather than px-4 — the
          // width went to the two dates, and the padding back to the pill,
          // so the column loses less room for its content than for itself.
          <th scope="col" className={cn(cell, "w-32 px-3 text-center")}>
            <HeaderFilter
              label="Status"
              options={statusFilterOptions}
              selected={selectedStatuses}
              onChange={setSelectedStatuses}
              align="center"
              triggerClassName="justify-center"
            />
          </th>
        )}
        {/* Wider than the flag it sits over, because the header is the sort
            control: "Priority" plus a numbered sort badge is what has to fit,
            and at the old 4rem the badge sat on top of the next column. */}
        <th scope="col" className={cn(cell, "w-[5.5rem] px-2 text-center")}>
          <HeaderFilter
            label="Priority"
            options={priorityFilterOptions}
            selected={selectedPriorities}
            onChange={setSelectedPriorities}
            align="center"
            sort={sortControlFor("priority", "Highest first", "Lowest first")}
            triggerClassName="justify-center"
          />
        </th>
        <th scope="col" className={cn(cell, "hidden w-40 px-4 text-center 2xl:table-cell")}>
          <HeaderFilter
            label="Added By"
            options={personFilterOptions}
            selected={selectedAddedBys}
            onChange={setSelectedAddedBys}
            searchPlaceholder="Search people..."
            align="center"
            triggerClassName="justify-center"
          />
        </th>
        {/* px-2, not the shared px-4: at these widths the cell's own padding
            was most of what a date had to fit into. The pair took the width
            Status gave up — enough that "Dec 31, 2025" clears its walls and
            that "Last updated" still fits beside its sort badge. */}
        {/* Centred, header and cell alike. A date is a fixed-width token, not
            prose: ranged left in a column wider than the longest date, the
            column reads as a ragged left edge with a gulf of air on the right,
            and the two date columns beside each other made that gulf twice. */}
        <th scope="col" className={cn(cell, "hidden w-28 text-center xl:table-cell")}>
          <HeaderDateFilter
            label="Created"
            sort={sortControlFor("created", "Oldest first", "Newest first")}
            range={createdRange}
            onRangeChange={setCreatedRange}
            triggerClassName="justify-center"
          />
        </th>
        <th scope="col" className={cn(cell, "hidden w-32 text-center xl:table-cell")}>
          <HeaderDateFilter
            label="Last updated"
            sort={sortControlFor("updated", "Oldest first", "Newest first")}
            range={updatedRange}
            onRangeChange={setUpdatedRange}
            triggerClassName="justify-center"
          />
        </th>
        {/* No `border-l`: `border-separate` does not collapse an adjacent pair
            of 1px borders, so this cell's left rule stacked with Last updated's
            right rule and that one boundary drew twice as thick as every other
            divider in the table. The cell on the left already emits it. */}
        {fieldColumns.map((field) => {
          const [asc, desc] = fieldSortLabels(field);
          return (
            <th key={field.id} scope="col" className={cn(cell, "hidden w-28 px-2 text-center xl:table-cell")}>
              <FieldColumnHeader
                field={field}
                sort={sortControlFor(fieldSortColumn(field.id), asc, desc)}
                filter={fieldFilters[field.id]}
                onFilterChange={(filter) => setFieldFilter(field.id, filter)}
              />
            </th>
          );
        })}
        {summaryFields && (
          <th scope="col" className={cn(cell, "hidden w-48 px-2 text-center xl:table-cell")}>
            <FieldsColumnHeader
              fields={summaryFields}
              sortFor={(field) => {
                const [asc, desc] = fieldSortLabels(field);
                return sortControlFor(fieldSortColumn(field.id), asc, desc);
              }}
              filters={fieldFilters}
              onFilterChange={setFieldFilter}
            />
          </th>
        )}
        <th data-vrule="off" className={cn(cell, "w-14 pr-3")} />
      </tr>
    );
  };

  const renderItemRow = (
    item: BacklogItem,
    revealIndex?: number,
    railColor?: string,
    // The block's own rows, in display order — what a shift-click range is
    // read off. It is passed in rather than derived because the same row
    // renderer draws two different tables.
    orderedRows: BacklogItem[] = expandedRows,
    withStatus = showStatusColumn,
    // The same ticket is drawn in its status block AND in All tickets, so a
    // FLIP id keyed on the item alone would name two rows at once. The caller
    // names the table.
    flipPrefix = "g",
    // Set while the row's section is closing: its place in the bottom-to-top
    // exit run (see `.row-collapse` and collapseDelayFor).
    collapseDelayMs?: number,
  ) => {
    const index = filteredIndexById.get(item.id) ?? 0;
    const isSelected = selectedItemIds.includes(item.id);
    const itemLinkCount = linkCounts?.get(item.id) ?? 0;
    const isLinksExpanded = expandedLinkIds.has(item.id);
    // Where this row sits in a bulk tick, if one is running (see
    // `.tick-pop`). Rows past the cap all pop together on the last step.
    const tickIndex = tickOrder?.get(item.id);
    const tickDelayMs = tickIndex === undefined ? undefined : Math.min(tickIndex, TICK_CASCADE_ROWS - 1) * TICK_STEP_MS;
    // On its way out of the list: fades where it stands for one beat, then
    // the rows below glide up over it (see useAnimatedList).
    const isExiting = exitingIds.has(item.id);
    const blockClassName = cn("group-block", isExiting && "row-exit");
    // The expanded-links sub-row leaves on the first beat of a closing run —
    // it hangs *below* its parent row, so in a bottom-to-top exit it cannot
    // outlive it. Its delay is the class default (0ms).
    const subRowBlockClassName = cn(blockClassName, collapseDelayMs !== undefined && "row-collapse");
    return (
      <React.Fragment key={item.id}>
        <BacklogRow
          item={item}
          displayRowNumber={index + 1}
          onUpdate={() => {}}
          onDuplicate={handleDuplicateItem}
          onDuplicateToPlatforms={handleDuplicateToPlatforms}
          openFromUrl={itemIdFromUrl === item.id}
          commentIdFromUrl={itemIdFromUrl === item.id ? commentIdFromUrl : undefined}
          onItemOpen={() => onItemOpen?.(item.id)}
          onItemClose={onItemClose ?? (() => {})}
          dropdownData={dropdownData}
          selectableStatuses={selectableStatuses}
          pipeline={item.pipeline_id ? pipelineById.get(item.pipeline_id) : undefined}
          showCheckbox={canBulkEdit}
          isSelected={isSelected}
          onCheckboxChange={(checked, shiftKey) => handleCheckboxChange(item.id, checked, shiftKey, orderedRows)}
          isAuthenticated={isAuthenticated}
          compact={compactRows}
          showStatusColumn={withStatus}
          isMine={myItemIds.has(item.id)}
          staleAfterMs={staleAfterMs}
          rowIndex={index}
          revealIndex={revealIndex}
          linkCount={itemLinkCount}
          onNavigateToItem={(linkedItemId) => navigate(`/item/${linkedItemId}`)}
          isLinksExpanded={isLinksExpanded}
          onToggleLinks={handleToggleLinkedItems}
          blockClassName={blockClassName}
          railColor={railColor}
          flipId={`${flipPrefix}:${item.id}`}
          tickDelayMs={tickDelayMs}
          collapseDelayMs={collapseDelayMs}
          fieldColumns={fieldColumns}
          summaryFields={summaryFields}
          fieldsFor={fieldsFor}
        />
        {isLinksExpanded && (
          <LinkedItemsSubRow
            itemId={item.id}
            dropdownData={dropdownData}
            showCheckbox={canBulkEdit}
            showStatusColumn={withStatus}
            onNavigateToItem={(linkedItemId) => navigate(`/item/${linkedItemId}`)}
            linkCounts={linkCounts}
            blockClassName={subRowBlockClassName}
            railColor={railColor}
          />
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-background">
      {/* Rung two of the page ladder that starts with Index's header. The
            wrapper carries the animation rather than the toolbar itself, so
            swapping the filter bar for the bulk bar mid-session doesn't
            replay the entrance — and `.toolbar-slot` (index.css) holds the two
            occupants to one height, so the swap doesn't move the table either. */}
      <div className="page-rise toolbar-slot" style={{ "--rise-delay": "80ms" } as CSSProperties}>
        {selectedItemIds.length > 0 && canBulkEdit ? (
          <BulkActionBar
            selectedCount={selectedItemIds.length}
            onMarkComplete={handleMarkComplete}
            onDelete={handleBulkDelete}
            statuses={bulkStatuses}
            onChangeStatus={handleBulkStatusChange}
            priorities={dropdownData?.priorities ?? EMPTY_ARRAY}
            onChangePriority={handleBulkPriorityChange}
            onClearSelection={() => {
              setSelectedItemIds([]);
              setTickOrder(null);
              lastCheckedIdRef.current = null;
            }}
            isAdmin={isAdmin}
          />
        ) : (
          <BacklogFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedTicketTypes={selectedTicketTypes}
            onTicketTypesChange={setSelectedTicketTypes}
            selectedTeams={selectedTeams}
            onTeamsChange={setSelectedTeams}
            selectedPipelines={selectedPipelines}
            onPipelinesChange={setSelectedPipelines}
            pipelines={pipelines}
            selectedAddedBys={selectedAddedBys}
            onAddedBysChange={setSelectedAddedBys}
            // The same alphabetical people list the header dropdowns use, so
            // the two facets can't disagree about who exists.
            people={personFilterOptions}
            currentUserId={currentUserId}
            ticketTypes={dropdownData?.ticketTypes ?? EMPTY_ARRAY}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={handleClearFilters}
            isAuthenticated={isAuthenticated}
            onAddItem={() => handleAddItem(1)}
            presets={presets}
            onSavePreset={(name) => {
              const saved = savePreset(name, {
                searchQuery,
                selectedAssignees,
                selectedStatuses,
                selectedPriorities,
                selectedAddedBys,
                selectedTicketTypes,
                selectedTeams,
                selectedPipelines,
                customFields: fieldFilters,
              });
              if (saved) {
                toast.success(`View "${name}" saved`);
              } else {
                toast.error("Maximum views reached (10)");
              }
              return saved;
            }}
            onLoadPreset={(preset) => loadFilters(preset.filters)}
            onDeletePreset={deletePreset}
            viewSwitcherSlot={
              <>
                {highlightSwitcher}
                {viewSwitcher}
                {exportButton}
              </>
            }
          />
        )}
      </div>

      {/* Rung three. The rise sits on the scroll container itself rather
            than on a wrapper inside it: everything sticky in this table (the
            thead, the action column) positions against *this* element's
            scrollport, and a transform on an element between a scrollport and
            its sticky descendants is what breaks stickiness — a transform on
            the scrollport is not one. Checked in the running app after the
            entrance: the header still pins to the top and the action column
            still holds the right edge under both scroll axes. */}
      <div
        ref={tableScrollRef}
        style={{ "--rise-delay": "140ms" } as CSSProperties}
        // `scrollbar-gutter: stable` reserves the vertical scrollbar's channel
        // whether or not one is currently drawn. Without it the list is one
        // width with every section collapsed and a scrollbar's width narrower
        // the moment one is opened, so the table visibly snapped in as the rows
        // arrived — the reader's own click appearing to resize the page. Anyone
        // whose OS draws overlay scrollbars never saw it; "always show scroll
        // bars" is the setting that bites, and this covers both.
        className="page-rise relative min-h-0 flex-1 overflow-auto px-3 [scrollbar-gutter:stable] max-lg:px-2 lg:px-6"
      >
        {/* ---- All tickets ----
              Everything the filters left, ungrouped, and it leads the list.
              Grouping answers "what is in each state?"; sometimes the question
              is just "what is there?", and that is the question a reader
              arrives with — so the flat read is the first thing on the page
              rather than the thing under five collapsed sections. It is also
              the one place the Status column can be shown while the blocks
              below are still statuses.

              It is a SEPARATE table for exactly that reason. `table-fixed`
              takes one column set for the whole table, so a block carrying a
              column the rest of the table doesn't have cannot live in it. The
              cost is that this block's columns line up with each other but not
              with the blocks below, which is the right trade: nobody reads
              across a collapsed section boundary, and the alternative was
              giving up the column that makes this view worth opening. */}
        {/* Below `lg` this ONE block gets a scroller of its own. It is the only
              table on the page carrying Status at that width, and 94px of pill
              is 94px a 375px row does not have: without a floor under Task the
              auto column collapses to nothing and the header labels print over
              each other. So Task keeps a readable minimum here and the block
              scrolls sideways to pay for it — the page itself never does, and
              the grouped blocks below still fit whole. Dropping Status instead
              would have left this block indistinguishable from them, which is
              the one thing it exists not to be. */}
        {groupBy === "status" && displayItems.length > 0 && (
          <div className={cn(allTicketsCompact && "-mx-2 overflow-x-auto px-2")}>
            <table
              // The narrow-window floor is a min-width on the TABLE, never a
              // width on Task — see `compactMinWidth`. Task is the auto column
              // at every width, so it is the only column that ever changes size.
              style={allTicketsCompact ? { minWidth: compactMinWidth(canBulkEdit, true) } : undefined}
              className="relative mt-3 w-full table-fixed border-separate border-spacing-0"
            >
              {/* This block's own sizing row — same widths as the main table's,
                    plus the Status column it exists to show. */}
              <thead aria-hidden className="h-0">
                <tr className="h-0">
                  {canBulkEdit && <th className="h-0 w-11 p-0" />}
                  <th className="h-0 w-16 p-0 max-lg:w-12" />
                  <th className="h-0 w-[6.75rem] p-0 max-lg:w-20 xl:w-28" />
                  <th className="h-0 p-0" />
                  <th className="h-0 w-[6.5rem] p-0 max-lg:w-[5.875rem] xl:w-32" />
                  <th className="h-0 w-[4.5rem] p-0 max-lg:w-16 xl:w-[5.5rem]" />
                  <th className="hidden h-0 w-40 p-0 2xl:table-cell" />
                  <th className="hidden h-0 w-28 p-0 xl:table-cell" />
                  <th className="hidden h-0 w-32 p-0 xl:table-cell" />
                  {fieldColumns.map((f) => (
                    <th key={f.id} className="hidden h-0 w-28 p-0 xl:table-cell" />
                  ))}
                  {summaryFields && <th className="hidden h-0 w-48 p-0 xl:table-cell" />}
                  <th className="h-0 w-14 p-0 max-lg:w-11" />
                </tr>
              </thead>
              <tbody>
                <tr
                  data-flip-id="all:header"
                  style={allTicketsRailStyle}
                  className={cn("group-block group-block-top", !allTicketsOpen && "group-block-bottom")}
                >
                  <td
                    colSpan={allTicketsColSpan}
                    className="border-b border-[hsl(var(--table-hairline))] bg-card px-2 py-1.5"
                  >
                    <div className="flex w-fit items-center gap-1">
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() =>
                          toggleSection(ALL_TICKETS_KEY, allTicketsOpen, displayItems.length, () =>
                            setAllTicketsOpen((v) => !v),
                          )
                        }
                        // Mid-close reads as shut, exactly as a grouped section
                        // does: the click's feedback must not wait out the run.
                        aria-expanded={allTicketsOpen && !allTicketsClosing}
                        className="press-pop hover-wash flex items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors"
                      >
                        <ChevronDown
                          className={cn(
                            "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200",
                            (!allTicketsOpen || allTicketsClosing) && "-rotate-90",
                          )}
                        />
                        <ListChecks className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <span className="text-[13px] font-bold text-foreground">All tickets</span>
                        {/* The count and nothing else. The line of prose that
                            used to sit here ("every ticket above, in one list")
                            described the block's position rather than its
                            contents, and the block has since moved to the top —
                            the title and the count already say what it is. */}
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold text-muted-foreground">
                          {displayItems.length}
                        </span>
                      </button>
                    </div>
                  </td>
                </tr>
                {allTicketsOpen &&
                  renderColumnHeaderRow(
                    allTicketsGroup,
                    allTicketsRailStyle,
                    true,
                    false,
                    allTicketsClosing ? collapseCeilingDelay(displayItems.length) : undefined,
                  )}
                {allTicketsOpen &&
                  displayItems.map((item, i) =>
                    renderItemRow(
                      item,
                      i,
                      NEUTRAL_RAIL,
                      displayItems,
                      true,
                      "all",
                      allTicketsClosing ? collapseDelayFor(i, displayItems.length) : undefined,
                    ),
                  )}
                {allTicketsOpen && (
                  <tr
                    aria-hidden
                    data-flip-id="all:footer"
                    style={allTicketsRailStyle}
                    className={cn(
                      "group-block group-block-bottom",
                      // The floor is the bottom-most thing in the block, so it
                      // leads the exit (the class's own 0ms delay).
                      allTicketsClosing && "row-collapse",
                    )}
                  >
                    <td
                      colSpan={allTicketsColSpan}
                      className="h-2 border-b border-[hsl(var(--table-hairline))] bg-card p-0"
                    />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* table-fixed is load-bearing: with auto layout the columns re-fit
              to whatever rows are visible, so expanding a section shoved the
              header cells sideways. Fixed layout sizes columns from the thead
              alone — the headers stay put no matter what the body shows. The
              responsive min-widths keep the fixed columns from squeezing into
              each other when the container is narrower than their sum (the
              wrapper scrolls horizontally instead); each step matches the
              columns visible at that breakpoint. */}
        {/* Below `lg` that fallback stops being a fallback and becomes the
              layout. Six columns and a 375px window leave the auto Task column
              LESS THAN NOTHING — measured 0px, so every row showed a type dot
              and a comment button and no ticket name at all, which is not a
              table. The TABLE therefore takes a floor at that width (see
              `compactMinWidth`; it is the table's and not Task's for the reason
              written there) and the block swipes sideways for the last ~60px. A
              block you can push with a thumb is a working list; a row that
              cannot say what its ticket is called is the bug this pass exists to
              fix. The PAGE still never scrolls sideways — only the block does. */}
        <div className={cn(groupedCompact && "-mx-2 overflow-x-auto px-2")}>
          <table
            // As above: the floor is the table's, so Task stays the auto column
            // and Priority / the action column / the checkbox hold their widths.
            style={groupedCompact ? { minWidth: compactMinWidth(canBulkEdit, showStatusColumn) } : undefined}
            className="relative w-full table-fixed border-separate border-spacing-0"
          >
            {/* A sizing row, not a header. `table-fixed` reads its column
                  widths off the first row of the table, and the real column
                  headers now live inside each expanded block — so this row
                  exists only to carry those widths (and the breakpoints that
                  drop a column), at zero height and out of the a11y tree.

                  It is also the ONLY place the widths are decided: fixed layout
                  ignores widths on every later row, so the `w-*` classes further
                  down are documentation. Task deliberately carries none, at any
                  width — an auto column in a fixed layout takes whatever the
                  sized columns leave, which is what keeps the table inside its
                  container instead of scrolling sideways, AND what keeps every
                  other column at the width named here. A fixed table with no
                  auto column hands its leftover width out proportionally over
                  all of them, which is how Priority and the action column used
                  to grow with the window. */}
            <thead aria-hidden className="h-0">
              <tr className="h-0">
                {canBulkEdit && <th className="h-0 w-11 p-0" />}
                <th className="h-0 w-16 p-0 max-lg:w-12" />
                <th className="h-0 w-[6.75rem] p-0 max-lg:w-20 xl:w-28" />
                <th className="h-0 p-0" />
                {showStatusColumn && <th className="h-0 w-[6.5rem] p-0 max-lg:w-[5.875rem] xl:w-32" />}
                <th className="h-0 w-[4.5rem] p-0 max-lg:w-16 xl:w-[5.5rem]" />
                <th className="hidden h-0 w-40 p-0 2xl:table-cell" />
                <th className="hidden h-0 w-28 p-0 xl:table-cell" />
                <th className="hidden h-0 w-32 p-0 xl:table-cell" />
                {fieldColumns.map((f) => (
                  <th key={f.id} className="hidden h-0 w-28 p-0 xl:table-cell" />
                ))}
                {summaryFields && <th className="hidden h-0 w-48 p-0 xl:table-cell" />}
                {/* One kebab wide. The three hover icons this replaced needed
                      8rem of every row for actions most rows never take. */}
                <th className="h-0 w-14 p-0 max-lg:w-11" />
              </tr>
            </thead>
            {displayItems.length === 0 && (
              <tbody>
                {/* The same gap row every section opens with, so the empty
                      card clears the toolbar's rule instead of sitting on it. */}
                <tr aria-hidden>
                  <td colSpan={tableColSpan} className="h-3 p-0" />
                </tr>
                <tr className="group-block group-block-top group-block-bottom">
                  <td
                    colSpan={tableColSpan}
                    className="border-b border-[hsl(var(--table-hairline))] bg-card px-4 py-12 text-center"
                  >
                    <div className="flex flex-col items-center gap-3">
                      <p className="text-muted-foreground">
                        {localItems.length === 0 ? "No items in the backlog yet" : "No items match your filters"}
                      </p>
                      {localItems.length === 0 && isAuthenticated && (
                        <Button size="sm" onClick={() => handleAddItem(1)} className="gap-2">
                          <Plus className="h-4 w-4" />
                          Add first item
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              </tbody>
            )}
            {/* Sections for the current grouping, each collapsible behind a
                  marker that suits the dimension: a coloured ring for a status,
                  an avatar for a person, a tile for a department.

                  One <tbody> per section: each block is a row group in the
                  markup as well as on screen, which is what makes its own
                  column-header row a header for those rows and not for the
                  table. */}
            {displayItems.length > 0 &&
              groups.map((group) => {
                const collapsed = !expandedGroups.has(group.key);
                // Mid-close: rows still mounted, playing the bottom-to-top exit.
                // The header chrome (chevron, aria, Add task) already reads as
                // collapsed — the click's feedback must not wait for the rows.
                const closing = closingGroups.has(group.key);
                const rail = railColorOf(group);
                const railStyle = { "--group-rail": rail } as CSSProperties;
                // Filing straight into the section you are reading only
                // means anything when the sections *are* statuses.
                const canAddHere = isAuthenticated && groupBy === "status" && group.key !== "__none__" && !closing;
                // A filter that took every row in a section takes the section
                // with it, so its chrome leaves on the same beat its rows do
                // rather than popping out from under them.
                const groupExiting = group.items.every((item) => exitingIds.has(item.id));
                return (
                  <tbody key={group.key}>
                    {/* The gap between blocks. Rows can't carry margins, so
                            the air between two cards is a row of its own. */}
                    <tr aria-hidden data-flip-id={`gap:${group.key}`}>
                      <td colSpan={tableColSpan} className="h-3 p-0" />
                    </tr>
                    <tr
                      data-flip-id={`gh:${group.key}`}
                      style={railStyle}
                      className={cn(
                        "group-block group-block-top",
                        collapsed && "group-block-bottom",
                        groupExiting && "row-exit",
                      )}
                    >
                      <td
                        colSpan={tableColSpan}
                        className="border-b border-[hsl(var(--table-hairline))] bg-card px-2 py-1.5"
                      >
                        {/* Pinned to the left edge: the header stays read-
                                  able however far the columns are scrolled, and
                                  a full-width hit area can't drag focus sideways. */}
                        <div className="flex w-fit items-center gap-1">
                          <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => toggleGroup(group.key, group.items.length)}
                            aria-expanded={!collapsed && !closing}
                            // --muted is identical to --background in light
                            // mode, so a muted hover paints nothing; the
                            // brand tint is the one that actually reads.
                            // --secondary was that tint but far too faint to
                            // see on a header this size, so these now take the
                            // app's shared `.hover-wash` (ring at 15%) —
                            // darker, and one place to tune it for everything.
                            className="press-pop hover-wash flex items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors"
                          >
                            <ChevronDown
                              className={cn(
                                "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200",
                                (collapsed || closing) && "-rotate-90",
                              )}
                            />
                            {group.profile ? (
                              <UserAvatar
                                avatarUrl={group.profile.avatar_url}
                                firstName={group.profile.first_name}
                                lastName={group.profile.last_name}
                                email={group.profile.email}
                                className="h-5 w-5"
                                fallbackClassName="text-[9px]"
                              />
                            ) : group.deptIndex !== undefined ? (
                              <span
                                aria-hidden
                                className={cn(
                                  "flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded text-[9px] font-bold uppercase text-white",
                                  DEPT_TILE_COLORS[group.deptIndex % DEPT_TILE_COLORS.length],
                                )}
                              >
                                {group.label.charAt(0)}
                              </span>
                            ) : (
                              <span
                                aria-hidden
                                className="h-3 w-3 shrink-0 rounded-full border-[2.5px]"
                                style={{ borderColor: group.color }}
                              />
                            )}
                            <span className="text-[13px] font-bold text-foreground">{group.label}</span>
                            <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold text-muted-foreground">
                              {group.items.length}
                            </span>
                          </button>
                          {/* TAKT's signature affordance, now part of the
                                  header the section is filed under — a sibling
                                  of the toggle, not nested inside it. */}
                          {!collapsed && canAddHere && (
                            <button
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handleAddItem(undefined, group.key)}
                              className="press-pop hover-wash flex items-center gap-1.5 rounded-md px-2 py-1 text-[12.5px] font-semibold text-muted-foreground transition-colors hover:text-brand"
                            >
                              <Plus className="h-3.5 w-3.5" />
                              Add task
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* The column headers belong to the block, not to the
                          page: they appear when a section is open and nowhere
                          when everything is shut. Every block's filter reads and
                          writes the same filter state, so which block you set it
                          from doesn't matter. */}
                    {!collapsed &&
                      renderColumnHeaderRow(
                        group,
                        railStyle,
                        showStatusColumn,
                        groupExiting,
                        // The ceiling leaves last: one step past the topmost row.
                        closing ? collapseCeilingDelay(group.items.length) : undefined,
                      )}
                    {!collapsed &&
                      group.items.map((item, i) =>
                        renderItemRow(
                          item,
                          i,
                          rail,
                          expandedRows,
                          showStatusColumn,
                          "g",
                          closing ? collapseDelayFor(i, group.items.length) : undefined,
                        ),
                      )}
                    {/* The block's floor. A row of its own rather than the
                          last item row's edge, because what the last row *is*
                          changes — expanding a row's links appends more. */}
                    {!collapsed && (
                      <tr
                        aria-hidden
                        data-flip-id={`gf:${group.key}`}
                        style={railStyle}
                        className={cn(
                          "group-block group-block-bottom",
                          groupExiting && "row-exit",
                          // The floor is the bottom-most thing in the block, so
                          // it leads the exit (the class's own 0ms delay).
                          closing && "row-collapse",
                        )}
                      >
                        <td
                          colSpan={tableColSpan}
                          className="h-2 border-b border-[hsl(var(--table-hairline))] bg-card p-0"
                        />
                      </tr>
                    )}
                  </tbody>
                );
              })}
          </table>
        </div>

        {/* Recently deleted trails the live sections: a collapsed
              holding pen at the bottom of the queue it came from, instead
              of a separate page. Rows are deliberately flat — one line,
              struck through — so a deleted ticket can't be mistaken for
              a live one.

              Its own table as well, and a one-column one: every row here is a
              single full-width cell, so it never needed the column set above
              and only sat inside it because it used to be the last tbody. */}
        {isAuthenticated && deletedItems.length > 0 && (
          <table className="mt-8 w-full border-separate border-spacing-0">
            <tbody>
              <tr
                style={{ "--group-rail": "hsl(var(--muted-foreground) / 0.45)" } as CSSProperties}
                className={cn("group-block group-block-top", !showDeleted && "group-block-bottom")}
              >
                <td className="border-b border-[hsl(var(--table-hairline))] bg-card px-2 py-1.5">
                  <div className="w-fit">
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() =>
                        toggleSection(DELETED_KEY, showDeleted, deletedItems.length, () => setShowDeleted((v) => !v))
                      }
                      aria-expanded={showDeleted && !deletedClosing}
                      className="press-pop hover-wash flex items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors"
                    >
                      <ChevronDown
                        className={cn(
                          "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200",
                          (!showDeleted || deletedClosing) && "-rotate-90",
                        )}
                      />
                      <Trash2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="text-[13px] font-bold text-muted-foreground">Recently deleted</span>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold text-muted-foreground">
                        {deletedItems.length}
                      </span>
                      <span className="hidden text-[11px] font-medium text-muted-foreground/70 sm:inline">
                        cleared after 30 days
                      </span>
                    </button>
                  </div>
                </td>
              </tr>
              {showDeleted &&
                deletedItems.map((item, i) => (
                  <tr
                    key={item.id}
                    className={cn(
                      "cascade-item group-block bg-card [&>td]:border-b [&>td]:border-[hsl(var(--table-hairline))]",
                      // Declared after `.cascade-item` in index.css, so on the
                      // way out its animation is the one that plays.
                      deletedClosing && "row-collapse",
                    )}
                    style={
                      {
                        animationDelay: deletedClosing
                          ? `${collapseDelayFor(i, deletedItems.length)}ms`
                          : `${30 + Math.min(i, 8) * 40}ms`,
                        "--group-rail": "hsl(var(--muted-foreground) / 0.45)",
                      } as CSSProperties
                    }
                  >
                    <td className="px-4 py-1.5">
                      <div className="flex max-w-4xl items-center gap-3">
                        <span className="w-10 shrink-0 text-center text-xs text-muted-foreground/70">
                          {item.ticket_number}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground line-through">
                          {item.task}
                        </span>
                        <span className="shrink-0 text-xs text-muted-foreground/70">
                          deleted {deletedAgo(item.deleted_at)}
                        </span>
                        {isAdmin && (
                          <span className="flex shrink-0 items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 gap-1.5 px-2 text-xs font-semibold text-brand"
                              disabled={restoreMutation.isPending}
                              onClick={() => handleRestore(item)}
                            >
                              <RotateCcw className="h-3.5 w-3.5" />
                              Restore
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Delete permanently"
                              className="h-7 w-7 text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                              disabled={permanentDeleteMutation.isPending}
                              onClick={() => setPermanentDeleteTarget(item)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              {showDeleted && (
                <tr
                  aria-hidden
                  style={{ "--group-rail": "hsl(var(--muted-foreground) / 0.45)" } as CSSProperties}
                  className={cn("group-block group-block-bottom", deletedClosing && "row-collapse")}
                >
                  <td className="h-2 border-b border-[hsl(var(--table-hairline))] bg-card p-0" />
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {showAddDialog && (
        <Suspense fallback={null}>
          <AddItemDialog
            open={showAddDialog}
            onOpenChange={setShowAddDialog}
            onSuccess={handleItemCreated}
            insertPosition={insertPosition}
            subsheetId={subsheetId}
            initialStatusId={addStatusId}
          />
        </Suspense>
      )}

      {duplicateToPlatformsSource && (
        <Suspense fallback={null}>
          <DuplicateToPlatformsDialog
            open={!!duplicateToPlatformsSource}
            onOpenChange={(isOpen) => {
              if (!isOpen) setDuplicateToPlatformsSource(null);
            }}
            source={duplicateToPlatformsSource}
          />
        </Suspense>
      )}

      {/* Permanent deletion from the Recently deleted section — the one
            delete in the app that truly cannot be undone. */}
      <DeleteConfirmationDialog
        open={!!permanentDeleteTarget}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPermanentDeleteTarget(null);
        }}
        onConfirm={handlePermanentDelete}
        items={
          permanentDeleteTarget
            ? [
                {
                  id: permanentDeleteTarget.id,
                  task: permanentDeleteTarget.task,
                  ticket_number: permanentDeleteTarget.ticket_number,
                },
              ]
            : []
        }
      />
    </div>
  );
};

export default BacklogTable;
